import { isValidHexColor } from "./color.js";
import type { PaletteSwatchColors, PaletteTemplate } from "./types.js";

export function validatePaletteTemplate(template: PaletteTemplate): void {
  const expected = template.rows * template.columns;
  if (template.swatches.length !== expected) {
    throw new Error(
      `Palette "${template.id}" has ${template.swatches.length} swatches, expected rows * columns = ${expected}.`,
    );
  }

  const seen = new Set<number>();
  for (const swatch of template.swatches) {
    if (!Number.isInteger(swatch.index) || swatch.index < 0) {
      throw new Error(`Palette "${template.id}" has an invalid swatch index: ${swatch.index}.`);
    }
    if (seen.has(swatch.index)) throw new Error(`Palette "${template.id}" has duplicate swatch index ${swatch.index}.`);
    seen.add(swatch.index);
    if (!Number.isInteger(swatch.row) || !Number.isInteger(swatch.column)) {
      throw new Error(`Swatch ${swatch.index} in palette "${template.id}" has a non-integer row or column.`);
    }
    if (swatch.row < 0 || swatch.row >= template.rows || swatch.column < 0 || swatch.column >= template.columns) {
      throw new Error(`Swatch ${swatch.index} in palette "${template.id}" is outside the configured grid.`);
    }
    if (!isValidHexColor(swatch.baseHex)) {
      throw new Error(`Swatch ${swatch.index} in palette "${template.id}" has invalid baseHex "${swatch.baseHex}".`);
    }
    if (swatch.width <= 0 || swatch.height <= 0) {
      throw new Error(`Swatch ${swatch.index} in palette "${template.id}" must have positive width and height.`);
    }
  }
}

export function validateImageDimensions(template: PaletteTemplate, image: { naturalWidth: number; naturalHeight: number }): void {
  if (image.naturalWidth !== template.imageWidth || image.naturalHeight !== template.imageHeight) {
    throw new Error(
      `Palette "${template.id}" image dimensions are ${image.naturalWidth}x${image.naturalHeight}, `
      + `but config expects ${template.imageWidth}x${template.imageHeight}.`,
    );
  }
}

export function validateSwatchColors(template: PaletteTemplate, swatchColors?: PaletteSwatchColors): void {
  if (!swatchColors) return;
  const indexes = new Set(template.swatches.map((swatch) => swatch.index));
  for (const [rawIndex, hex] of Object.entries(swatchColors)) {
    const index = Number(rawIndex);
    if (!Number.isInteger(index) || !indexes.has(index)) {
      throw new Error(`Palette "${template.id}" has a swatch color for unknown swatch index "${rawIndex}".`);
    }
    if (!isValidHexColor(hex)) {
      throw new Error(`Palette "${template.id}" has invalid swatch color "${hex}" for index ${rawIndex}.`);
    }
  }
}
